import {createLiveSessionScope} from "../context/createLiveSessionScope";
import {useCallback, useEffect, useState} from "react";
import {subscribePublicOrganization} from "../services/publicOrganizationReader";
import {getOrganizationLookupCandidates} from "../utils/organizationAliases";
import {useResolvedOrgContext} from "../context/ResolvedOrgContext";
import {connectionFailure} from "../utils/connectionErrors";

export function orgIdentityMatchesSlug(org, slug) {
  return Boolean(org && getOrganizationLookupCandidates(slug).includes(org.id));
}
function usePublicOrganizationSource(orgSlug, {previewOrg = null, enabled = true} = {}) {
  const context=useResolvedOrgContext();
  const readOnly=context?.readOnlyWebsitePreview===true;
  const candidate=previewOrg||(readOnly&&(!orgSlug||orgSlug===context.orgId)?context.previewOrganization:null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  const [state, setState] = useState({slug: orgSlug, org: null, loading: Boolean(orgSlug), error: ""});
  useEffect(() => {
    const reconnect = () => { if (state.slug === orgSlug && state.errorKind === "connection" && !state.loading) retry(); };
    window.addEventListener("online", reconnect);
    return () => window.removeEventListener("online", reconnect);
  }, [state.slug, state.errorKind, state.loading, orgSlug, retry]);
  useEffect(() => {
    if (!enabled || readOnly || candidate) return undefined;
    setState({slug: orgSlug, org: null, loading: Boolean(orgSlug), error: ""});
    if (!orgSlug) return undefined;
    return subscribePublicOrganization(orgSlug,
      (org) => setState({slug: orgSlug, org, loading: false, missing: !org, error: org ? "" : "Website is not available."}),
      (error) => {
        const errorKind = connectionFailure(error);
        setState({slug: orgSlug, org: null, loading: false, errorKind, error: errorKind === "verification"
          ? "Website security verification could not finish. Please retry."
          : "Unable to load the published website. Please retry."});
      });
  }, [orgSlug, candidate, readOnly, attempt, enabled]);
  if (candidate) return {org: candidate, loading: false, error: ""};
  if (readOnly) return {org:null,loading:false,error:'This page is not part of the saved preview.'};
  // Quarantine previous-tenant state synchronously, including its error text.
  return {...(state.slug === orgSlug ? state : {org: null, loading: Boolean(orgSlug), error: ""}), retry};
}

const publicSession = createLiveSessionScope(usePublicOrganizationSource);
export const PublicOrganizationProvider = publicSession.Provider;
export default function usePublicOrganization(orgSlug, {previewOrg = null} = {}) {
  const context = useResolvedOrgContext();
  const preview = Boolean(previewOrg || context?.readOnlyWebsitePreview);
  const shared = publicSession.useShared(orgSlug || "", [orgSlug], !preview && Boolean(orgSlug));
  const local = usePublicOrganizationSource(orgSlug, {previewOrg, enabled: !shared.provided || preview});
  return shared.provided && !preview && orgSlug
    ? shared.value || {org: null, loading: true, error: "", retry: () => {}}
    : local;
}
